import React, { useState, useEffect, useCallback } from 'react';
import { X, Shield, Loader2, RefreshCw } from 'lucide-react';
import SearchableSelect from './SearchableSelect';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function authHeaders(json = true) {
  const token = localStorage.getItem('auth_token');
  const h = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

// Vehicle categories + GEL prices, mirrors backend CATEGORY_MAP.
const VEHICLE_TYPES = [
  { value: 'passenger_car', label: 'Passenger Car', prices: { days_15: 30, days_30: 50, days_90: 90, year_1: 295 } },
  { value: 'truck',         label: 'Truck',          prices: { days_15: 60, days_30: 100, days_90: 170, year_1: 610 } },
  { value: 'bus',           label: 'Bus',            prices: { days_15: 45, days_30: 75, days_90: 140, year_1: 480 } },
  { value: 'motorcycle',    label: 'Motorcycle',     prices: { days_15: 20, days_30: 35, days_90: 70, year_1: 215 } },
  { value: 'trailer',       label: 'Trailer',        prices: { days_15: 14, days_30: 25, days_90: 40, year_1: 145 } },
  { value: 'special_vehicle', label: 'Special Vehicle', prices: { days_15: 25, days_30: 45, days_90: 70, year_1: 250 } },
];

const PERIOD_LABELS = { days_15: '15 days', days_30: '30 days', days_90: '90 days', year_1: '1 year' };

// Fixed organisations for Legal-entity insurer (with tax IDs), from the Django form.
const ORGS = [
  { title: 'TTS LLC', taxId: '00534543' },
  { title: 'TTS Georgia LLC', taxId: '406553929' },
];

const label = 'block text-sm font-medium text-gray-700 mb-1';
const inp = 'block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

export default function TplIssueModal({ preorder, defaultVehicleType, onClose, onIssued }) {
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingRef, setLoadingRef] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { payment_url }
  const [policies, setPolicies] = useState([]);
  const [prefilled, setPrefilled] = useState(false);
  const [refError, setRefError] = useState('');

  const [form, setForm] = useState({
    plate_number: preorder.plate_number || '',
    start_date: preorder.start_date || '',
    period: preorder.insurance_ge || 'year_1',
    vehicle_type: defaultVehicleType || 'truck',
    vin: '',
    manufacturer_id: '',
    model_id: '',
    insurer_type: 'L',            // Legal entity by default (like the Django form)
    insurer_country_id: '51',     // Georgia
    insurer_title: ORGS[0].title,
    insurer_id: ORGS[0].taxId,
    insurer_phone: '37477029696',
    insurer_email: 'info@tts.am',
    bank: 'liberty',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Load reference data (manufacturers + countries) + prefill from existing
  // tpl.ge policies. Reference data is required — its failure shows a retry.
  const loadReferenceData = useCallback(async () => {
    setLoadingRef(true);
    setRefError('');
    try {
      const [mRes, cRes] = await Promise.all([
        fetch(`${BASE_URL}/pre-ordered-insurances/tpl-manufacturers/`, { headers: authHeaders(false) }),
        fetch(`${BASE_URL}/pre-ordered-insurances/tpl-countries/`, { headers: authHeaders(false) }),
      ]);
      if (!mRes.ok || !cRes.ok) throw new Error();
      setManufacturers(await mRes.json());
      setCountries(await cRes.json());
    } catch {
      setRefError('Failed to load data from tpl.ge. Check your connection and try again.');
      setLoadingRef(false);
      return;
    }

    // Real tpl.ge policies for this vehicle → prefill manufacturer/model/VIN.
    // Soft: a failure here just skips prefill, it does not block issuance.
    let tplVin = '';
    try {
      const pRes = await fetch(`${BASE_URL}/pre-ordered-insurances/${preorder.id}/tpl-policies/`, { headers: authHeaders(false) });
      if (pRes.ok) {
        const data = await pRes.json();
        const pols = data.policies || [];
        setPolicies(pols);
        const latest = pols[0];
        if (latest && latest.vehicleManufacturerId) {
          const mfrId = String(latest.vehicleManufacturerId);
          tplVin = latest.vinCode || '';
          setForm(prev => ({ ...prev, manufacturer_id: mfrId, vin: latest.vinCode || prev.vin }));
          setPrefilled(true);
          const modRes = await fetch(`${BASE_URL}/pre-ordered-insurances/tpl-models/${mfrId}/`, { headers: authHeaders(false) });
          if (modRes.ok) {
            setModels(await modRes.json());
            if (latest.vehicleModelId) setForm(prev => ({ ...prev, model_id: String(latest.vehicleModelId) }));
          }
        }
      }
    } catch {}

    // Fallback: no VIN from tpl.ge → use the vehicle's VIN from the fleet card.
    if (!tplVin && preorder.plate_number) {
      try {
        const vRes = await fetch(`${BASE_URL}/vehicles/admin-vehicles/?plate=${encodeURIComponent(preorder.plate_number)}`, { headers: authHeaders(false) });
        if (vRes.ok) {
          const list = await vRes.json();
          const veh = Array.isArray(list)
            ? list.find(v => (v.plate_number || '').toUpperCase() === (preorder.plate_number || '').toUpperCase())
            : null;
          if (veh && veh.vin) setForm(prev => ({ ...prev, vin: prev.vin || veh.vin }));
        }
      } catch {}
    }

    setLoadingRef(false);
  }, [preorder.id]);

  useEffect(() => { loadReferenceData(); }, [loadReferenceData]);

  // Cascade: load models when manufacturer changes.
  const loadModels = useCallback(async (manufacturerId) => {
    setModels([]);
    set('model_id', '');
    if (!manufacturerId) return;
    try {
      const res = await fetch(`${BASE_URL}/pre-ordered-insurances/tpl-models/${manufacturerId}/`, { headers: authHeaders(false) });
      if (res.ok) setModels(await res.json());
    } catch {}
  }, []);

  const price = (() => {
    const vt = VEHICLE_TYPES.find(v => v.value === form.vehicle_type);
    return vt?.prices?.[form.period];
  })();

  const isLegal = form.insurer_type === 'L';

  const handleSubmit = async () => {
    // Basic client validation before hitting tpl.ge.
    const required = ['insurer_title', 'insurer_id', 'insurer_phone', 'insurer_country_id', 'manufacturer_id', 'model_id', 'start_date'];
    const missing = required.filter(k => !form[k]);
    if (missing.length) { setError('Please fill all required fields: ' + missing.join(', ')); return; }

    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${BASE_URL}/pre-ordered-insurances/${preorder.id}/issue-tpl/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || (data.fields ? 'Missing: ' + data.fields.join(', ') : 'Request failed'));
      setResult(data);
      // Open the payment page in a new window.
      if (data.payment_url) window.open(data.payment_url, 'tpl_payment', 'width=900,height=700,scrollbars=yes,resizable=yes');
      if (onIssued) onIssued();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary-600" /> Issue TPL — {preorder.plate_number}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {result ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-green-800 font-semibold mb-1">✅ Policy created successfully!</div>
              <div className="text-sm text-green-700 mb-3">The payment page opened in a new window. Complete the payment there.</div>
              {result.payment_url && (
                <button onClick={() => window.open(result.payment_url, 'tpl_payment', 'width=900,height=700,scrollbars=yes,resizable=yes')}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700">↗ Reopen payment</button>
              )}
            </div>
          ) : (
            <>
              {loadingRef && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center gap-2 text-sm text-blue-700">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading data from tpl.ge…
                </div>
              )}
              {refError && !loadingRef && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-md flex items-center justify-between gap-3">
                  <div className="text-sm text-orange-800">{refError}</div>
                  <button type="button" onClick={loadReferenceData}
                    className="px-3 py-1.5 bg-orange-600 text-white rounded-md text-sm hover:bg-orange-700 whitespace-nowrap flex items-center gap-1.5">
                    <RefreshCw className="h-3.5 w-3.5" /> Retry
                  </button>
                </div>
              )}
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}

              {/* Existing tpl.ge policies for this vehicle */}
              {policies.length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="text-sm font-medium text-blue-800 mb-2">
                    {policies.length} existing tpl.ge {policies.length === 1 ? 'policy' : 'policies'} for this vehicle
                    {prefilled && <span className="ml-2 text-xs font-normal text-blue-600">· manufacturer / model / VIN prefilled from the latest</span>}
                  </div>
                  <div className="max-h-28 overflow-y-auto space-y-1">
                    {policies.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-xs text-gray-600">
                        <span className="font-medium">{p.policyNumber || '—'}</span>
                        <span>{(p.startDate || '?').slice(0, 10)} — {(p.endDate || '?').slice(0, 10)}</span>
                        <span className={`px-1.5 py-0.5 rounded ${p.policyActiveStatus === 'A' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                          {p.policyActiveStatus === 'A' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Policy details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-200">Policy Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={label}>Plate Number</label>
                    <input value={form.plate_number} readOnly className={`${inp} bg-gray-50 text-gray-700`} />
                  </div>
                  <div>
                    <label className={label}>Start Date *</label>
                    <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className={inp} />
                  </div>
                  <div>
                    <label className={label}>Period *</label>
                    <select value={form.period} onChange={e => set('period', e.target.value)} className={inp}>
                      {Object.entries(PERIOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={label}>Vehicle Type *</label>
                    <select value={form.vehicle_type} onChange={e => set('vehicle_type', e.target.value)} className={inp}>
                      {VEHICLE_TYPES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-3 px-4 py-2 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
                  <span className="text-sm font-semibold text-green-800">Policy Price:</span>
                  <span className="text-lg font-bold text-green-700">{price != null ? `${price} GEL` : '— GEL'}</span>
                </div>
              </div>

              {/* Vehicle details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-200">Vehicle Details</h4>
                <div className="space-y-4">
                  <div>
                    <label className={label}>VIN</label>
                    <input value={form.vin} onChange={e => set('vin', e.target.value)} placeholder="17 characters" className={inp} />
                  </div>
                  <div>
                    <label className={label}>Manufacturer *</label>
                    <SearchableSelect
                      placeholder={loadingRef ? 'Loading…' : '— Select manufacturer —'}
                      value={form.manufacturer_id}
                      options={manufacturers.map(m => ({ value: m.id, label: m.name || `#${m.id}` }))}
                      onChange={(mid) => { set('manufacturer_id', mid); loadModels(mid); }}
                    />
                  </div>
                  <div>
                    <label className={label}>Model *</label>
                    <SearchableSelect
                      placeholder={form.manufacturer_id ? '— Select model —' : '— Select manufacturer first —'}
                      value={form.model_id}
                      disabled={!form.manufacturer_id}
                      options={models.map(m => ({ value: m.id, label: m.name || `#${m.id}` }))}
                      onChange={(mid) => set('model_id', mid)}
                    />
                  </div>
                </div>
              </div>

              {/* Insurer details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-200">Insurer / Owner / Driver</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-6">
                    <span className="text-sm font-medium text-gray-700">Type:</span>
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="radio" checked={form.insurer_type === 'I'}
                        onChange={() => setForm(prev => ({ ...prev, insurer_type: 'I', insurer_title: '', insurer_id: '' }))} />
                      Individual
                    </label>
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="radio" checked={form.insurer_type === 'L'}
                        onChange={() => setForm(prev => ({ ...prev, insurer_type: 'L', insurer_title: ORGS[0].title, insurer_id: ORGS[0].taxId }))} />
                      Legal entity
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={label}>Country *</label>
                      <SearchableSelect
                        placeholder="— Select country —"
                        value={form.insurer_country_id}
                        options={countries.map(c => ({ value: c.id, label: c.name || `#${c.id}` }))}
                        onChange={(cid) => set('insurer_country_id', cid)}
                      />
                    </div>
                    <div>
                      <label className={label}>{isLegal ? 'Organisation Name *' : 'Full Name *'}</label>
                      {isLegal ? (
                        <select value={form.insurer_title}
                          onChange={e => {
                            const org = ORGS.find(o => o.title === e.target.value);
                            setForm(prev => ({ ...prev, insurer_title: e.target.value, insurer_id: org ? org.taxId : prev.insurer_id }));
                          }}
                          className={inp}>
                          {ORGS.map(o => <option key={o.title} value={o.title}>{o.title}</option>)}
                        </select>
                      ) : (
                        <input value={form.insurer_title} onChange={e => set('insurer_title', e.target.value)} placeholder="John Smith" className={inp} />
                      )}
                    </div>
                    <div>
                      <label className={label}>{isLegal ? 'Tax ID / Reg. Number *' : 'Passport / ID *'}</label>
                      <input value={form.insurer_id} onChange={e => set('insurer_id', e.target.value)} readOnly={isLegal}
                        className={`${inp} ${isLegal ? 'bg-gray-50' : ''}`} placeholder="12345678901" />
                    </div>
                    <div>
                      <label className={label}>Phone *</label>
                      <input value={form.insurer_phone} onChange={e => set('insurer_phone', e.target.value)} className={inp} />
                    </div>
                    <div>
                      <label className={label}>Email</label>
                      <input type="email" value={form.insurer_email} onChange={e => set('insurer_email', e.target.value)} className={inp} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-200">Payment Method</h4>
                <div className="flex gap-6">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" checked={form.bank === 'bog'} onChange={() => set('bank', 'bog')} /> Bank of Georgia
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" checked={form.bank === 'liberty'} onChange={() => set('bank', 'liberty')} /> Liberty Bank
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button onClick={handleSubmit} disabled={submitting || loadingRef || !!refError}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? 'Processing…' : loadingRef ? 'Loading…' : 'Proceed to Payment →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
